import { z } from 'zod';
/**
 * Time format validation (HH:MM)
 */
const TimeSchema = z
    .string()
    .regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format (00:00-23:59)');
/**
 * Working hours schema
 */
const WorkingHoursSchema = z
    .object({
    start: TimeSchema,
    end: TimeSchema,
    timezone: z.string().default('Asia/Baku')
})
    .refine((data) => data.start < data.end, {
    message: 'Start time must be before end time'
});
/**
 * Complete delivery policy schema
 */
export const DeliveryPolicySchema = z.object({
    workingHours: WorkingHoursSchema,
    slotDurationMinutes: z
        .number()
        .int()
        .min(30, 'Slot duration must be at least 30 minutes')
        .max(240, 'Slot duration cannot exceed 4 hours'),
    floristPreparationMinutes: z
        .number()
        .int()
        .min(30, 'Florist preparation time must be at least 30 minutes')
        .max(480, 'Florist preparation time cannot exceed 8 hours'),
    courierDeliveryMinutes: z
        .number()
        .int()
        .min(15, 'Courier delivery time must be at least 15 minutes')
        .max(240, 'Courier delivery time cannot exceed 4 hours'),
    expressDeliveryCharge: z
        .number()
        .min(0, 'Express delivery charge cannot be negative')
        .max(100, 'Express delivery charge cannot exceed 100 AZN'),
    defaultSlotCapacity: z
        .number()
        .int()
        .min(1, 'Default slot capacity must be at least 1')
        .max(100, 'Default slot capacity cannot exceed 100')
});
// Note: DeliveryPolicy type is defined in ./types/delivery.ts to avoid duplication
/**
 * Partial delivery policy schema (for updates/overrides)
 */
export const PartialDeliveryPolicySchema = DeliveryPolicySchema.partial();
/**
 * Delivery override schema
 */
export const DeliveryOverrideSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    priority: z.number().int().default(0),
    enabled: z.boolean().default(true),
    isHoliday: z.boolean().default(false),
    startDate: z.date().nullable().optional(),
    endDate: z.date().nullable().optional(),
    daysOfWeek: z.array(z.number().int().min(0).max(6)).nullable().optional(),
    overrides: PartialDeliveryPolicySchema
});
// Note: DeliveryOverride type is defined in ./types/delivery.ts to avoid duplication
/**
 * Slot capacity schema
 */
export const SlotCapacitySchema = z.object({
    date: z.date(),
    timeSlot: z.string().regex(/^\d{2}:\d{2}-\d{2}:\d{2}$/, 'Time slot must be in format HH:MM-HH:MM'),
    maxOrders: z.number().int().min(1),
    currentOrders: z.number().int().min(0).default(0)
});
// Note: SlotCapacity type is defined in ./types/delivery.ts to avoid duplication
